import React, { useCallback, useEffect, useRef, useState } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { useTranslation } from "../i18n/I18nContext";

const ICON_BY_VARIANT = {
  danger: "mingcute:alert-fill",
  warning: "ph:warning-bold",
  info: "mingcute:information-fill",
};

/**
 * Substitui o `window.confirm()` nativo por um modal elegante, consistente
 * com o resto da aplicação (em vez do diálogo feio e não estilizável do
 * browser).
 *
 * Uso:
 *   const { confirm, ConfirmModal } = useConfirm();
 *   ...
 *   const handleRemover = async () => {
 *     if (!(await confirm(t("algo.confirm.remover")))) return;
 *     ...
 *   };
 *   ...
 *   return (
 *     <>
 *       ...
 *       {ConfirmModal}
 *     </>
 *   );
 *
 * `confirm(message, options)` devolve uma Promise<boolean>, tal como o
 * `window.confirm()` que substitui, para que os pontos de chamada existentes
 * só precisem de passar a fazer `await`.
 */
export const useConfirm = () => {
  const { t } = useTranslation();
  const [dialog, setDialog] = useState(null);
  const resolverRef = useRef(null);

  const resolve = useCallback((result) => {
    resolverRef.current?.(result);
    resolverRef.current = null;
    setDialog(null);
  }, []);

  const confirm = useCallback(
    (message, options = {}) =>
      new Promise((res) => {
        resolverRef.current = res;
        setDialog({
          message,
          title: options.title || t("global.confirm.title"),
          confirmLabel: options.confirmLabel || t("global.confirm"),
          cancelLabel: options.cancelLabel || t("global.cancel"),
          variant: options.variant || "danger",
        });
      }),
    [t]
  );

  useEffect(() => {
    if (!dialog) return undefined;
    document.body.style.overflow = "hidden";
    const handleKeyDown = (ev) => {
      if (ev.key === "Escape") resolve(false);
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.body.style.overflow = "";
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [dialog, resolve]);

  const ConfirmModal = dialog ? (
    <div
      className='position-fixed top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center'
      style={{ zIndex: 1080, background: "rgba(0, 0, 0, 0.5)" }}
      onMouseDown={(ev) => {
        if (ev.target === ev.currentTarget) resolve(false);
      }}
    >
      <div className='modal-dialog modal-dialog-centered modal-sm mx-16' role='dialog' aria-modal='true'>
        <div className='modal-content radius-16 bg-base border-0 shadow-lg'>
          <div className='modal-body text-center pt-40 px-24 pb-24'>
            <div
              className={`bg-${dialog.variant}-100 text-${dialog.variant}-600 w-72-px h-72-px rounded-circle d-flex justify-content-center align-items-center mx-auto mb-16`}
            >
              <Icon icon={ICON_BY_VARIANT[dialog.variant] || ICON_BY_VARIANT.danger} className='text-3xl' />
            </div>
            <h6 className='mb-8'>{dialog.title}</h6>
            <p className='text-secondary-light mb-0'>{dialog.message}</p>
          </div>
          <div className='modal-footer border-0 justify-content-center gap-8 pb-24 pt-0'>
            <button
              type='button'
              className='btn btn-neutral-500 border-neutral-100 px-24'
              onClick={() => resolve(false)}
            >
              {dialog.cancelLabel}
            </button>
            <button type='button' className={`btn btn-${dialog.variant} px-24`} onClick={() => resolve(true)} autoFocus>
              {dialog.confirmLabel}
            </button>
          </div>
        </div>
      </div>
    </div>
  ) : null;

  return { confirm, ConfirmModal };
};

export default useConfirm;
